import { create } from 'zustand';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import pushNotificationService from '../services/pushNotificationService';
import { useNotificationStore } from './notificationStore';
import { useSiteStore } from './siteStore';

// Whether a browser push notification should be shown for this alert: the
// user's own "Real-time mobile & browser alerts" preference must be on, AND
// the alert's site must not have notifications disabled (Admin > Site
// Settings > Enable Notifications, which explicitly covers push too). Falls
// back to allowing the push if the site isn't found yet, matching the
// backend's own default-enabled behavior.
function shouldShowPushForAlert(alert) {
  if (!useNotificationStore.getState().pushPreferenceEnabled) return false;
  const site = useSiteStore.getState().sites.find((s) => s.id === alert.siteId);
  if (site && site.notificationsEnabled === false) return false;
  return true;
}

let socket = null;

// Batches rapid bursts of alert:new events (e.g. many cameras firing within
// the same second) into a single state update / sound / native notification
// / notification-bell refresh per short window, instead of doing all of that
// once per alert. Without this, a burst of simultaneous alerts each
// independently update store state, refetch the notification bell, and
// create a native Notification — work that stacks up fast enough to hang or
// crash the tab.
const ALERT_BATCH_WINDOW_MS = 300;
let pendingAlertBatch = [];
let alertBatchTimer = null;

function flushAlertBatch(set, get) {
  const batch = pendingAlertBatch;
  pendingAlertBatch = [];
  alertBatchTimer = null;
  if (batch.length === 0) return;

  set((state) => ({
    activeAlerts: state.activeAlerts + batch.length,
    latestAlert: batch[batch.length - 1],
  }));

  // One sound per batch, at the highest severity present, rather than one
  // overlapping oscillator per alert.
  if (get().soundAlerts) {
    const highestSeverity = batch.some((a) => a.severity === 'CRITICAL')
      ? 'CRITICAL'
      : batch.some((a) => a.severity === 'HIGH')
        ? 'HIGH'
        : null;
    if (highestSeverity) alertAudioCtx.play(highestSeverity);
  }

  const pushable = batch.filter(shouldShowPushForAlert);
  if (pushable.length === 1) {
    try {
      pushNotificationService.showAlertNotification(pushable[0]);
    } catch (error) {
      // ignore push notification errors
    }
  } else if (pushable.length > 1) {
    // Collapse a burst into one summary notification instead of creating a
    // native Notification object per alert.
    const criticalCount = pushable.filter((a) => a.severity === 'CRITICAL').length;
    try {
      pushNotificationService.showNotification(`🚨 ${pushable.length} new safety alerts`, {
        body: criticalCount > 0
          ? `${criticalCount} CRITICAL — open the Alerts page to review`
          : 'Open the Alerts page to review',
        tag: 'alert-batch',
        requireInteraction: criticalCount > 0,
      });
    } catch (error) {
      // ignore push notification errors
    }
  }

  // Trigger notification bell / alerts grid refresh once for the whole
  // batch, not once per alert.
  get().triggerNotificationRefresh();
}

// Sound alert system — AudioContext must be created/resumed inside a user
// gesture (Chrome autoplay policy). We unlock it on first interaction so
// alert sounds work without a console warning.
const alertAudioCtx = {
  ctx: null,
  unlock() {
    try {
      if (!this.ctx) {
        this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (this.ctx.state === 'suspended') this.ctx.resume();
    } catch (e) {
      // ignore — AudioContext unavailable
    }
  },
  play(severity) {
    try {
      const ctx = this.ctx;
      if (!ctx || ctx.state !== 'running') return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      if (severity === 'CRITICAL') {
        // Urgent double-beep
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
        gain.gain.setValueAtTime(0.3, ctx.currentTime + 0.2);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.4);
      } else {
        // Single beep for HIGH
        osc.frequency.value = 660;
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.25);
      }
    } catch (e) {
      // ignore
    }
  },
};

// Unlock audio on first user gesture so alert beeps work without warnings
['click', 'keydown', 'touchstart'].forEach((evt) => {
  document.addEventListener(evt, () => alertAudioCtx.unlock(), { once: true, capture: true });
});

function getSoundAlertPref() {
  try {
    return localStorage.getItem('kensafety-sound-alerts') !== 'off';
  } catch (e) {
    return true; // Default to true if localStorage is blocked
  }
}

export const useSocketStore = create((set, get) => ({
  connected: false,
  activeAlerts: 0,
  latestAlert: null,
  compliance: null,
  subscribedSite: null,
  deviceHealth: {}, // { deviceId: { cpu, gpu, temp, timestamp } }
  zoneOccupancy: {}, // { zoneId: { count, timestamp, ... } }
  cameraStatuses: {}, // { cameraId: { status, timestamp } }
  soundAlerts: getSoundAlertPref(),
  notificationCallbacks: [], // Callbacks for when notifications should be refreshed
  mailQuotaWarnings: [], // [{ siteId, siteName, count, cap, percentage }] — sites at/near their daily mail cap
  dismissedMailQuotaThresholds: {}, // { [siteId]: percentage } — don't re-show until % climbs past this

  dismissMailQuotaWarning: (siteId) => {
    set((state) => {
      const warning = state.mailQuotaWarnings.find((w) => w.siteId === siteId);
      return {
        mailQuotaWarnings: state.mailQuotaWarnings.filter((w) => w.siteId !== siteId),
        dismissedMailQuotaThresholds: {
          ...state.dismissedMailQuotaThresholds,
          [siteId]: warning?.percentage ?? 100,
        },
      };
    });
  },

  // Polling safety-net (see Layout.jsx) — only (re-)adds a site if its usage
  // climbed past whatever tier the user last dismissed, so a dismissal isn't
  // immediately undone by the next poll tick.
  mergeMailQuotaWarningsFromUsage: (usage) => {
    set((state) => {
      const byId = new Map(state.mailQuotaWarnings.map((w) => [w.siteId, w]));
      (usage || [])
        .filter((u) => u.mailCapEnabled && u.percentage >= 75)
        .filter((u) => u.percentage > (state.dismissedMailQuotaThresholds[u.siteId] ?? 0))
        .forEach((u) => {
          byId.set(u.siteId, {
            siteId: u.siteId,
            siteName: u.siteName,
            count: u.sentToday,
            cap: u.cap,
            percentage: u.percentage,
          });
        });
      return { mailQuotaWarnings: Array.from(byId.values()) };
    });
  },

  toggleSoundAlerts: () => {
    const next = !get().soundAlerts;
    try {
      localStorage.setItem('kensafety-sound-alerts', next ? 'on' : 'off');
    } catch (e) {
      // ignore if localStorage is blocked
    }
    set({ soundAlerts: next });
  },

  // Register callback to be called when new alert arrives (for notification refresh)
  onNewAlert: (callback) => {
    set((state) => ({
      notificationCallbacks: [...state.notificationCallbacks, callback],
    }));
    // Return unsubscribe function
    return () => {
      set((state) => ({
        notificationCallbacks: state.notificationCallbacks.filter(cb => cb !== callback),
      }));
    };
  },

  // Trigger all registered callbacks (called when alert:new arrives)
  triggerNotificationRefresh: () => {
    const callbacks = get().notificationCallbacks;
    callbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in notification callback:', error);
      }
    });
  },

  connect: () => {
    if (socket?.connected) return;

    // Clean up existing socket if any (prevents listener stacking on reconnect)
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
      socket = null;
    }

    // Drop any batch left over from a previous connection so it doesn't
    // flush against stale state.
    clearTimeout(alertBatchTimer);
    alertBatchTimer = null;
    pendingAlertBatch = [];

    let token = null;
    try {
      token = localStorage.getItem('token');
    } catch (e) {
      // ignore if localStorage is blocked
    }
    if (!token) return;

    // Note: Using default JSON parser for python-socketio compatibility
    socket = io('/dashboard', {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socket.on('connect', () => {
      set({ connected: true });

      // Re-subscribe to site if previously subscribed
      const siteId = get().subscribedSite;
      if (siteId) {
        socket.emit('subscribe:site', siteId);
      }
    });

    socket.on('disconnect', () => {
      set({ connected: false });
    });

    socket.on('connect_error', (err) => {
      console.error('Socket connect error:', err.message);
    });

    socket.io.on('reconnect', () => {
      const siteId = get().subscribedSite;
      if (siteId) {
        socket.emit('subscribe:site', siteId);
      }
    });

    socket.on('subscribed', () => {});

    // Handle new alerts — buffered into a short batch window (see
    // flushAlertBatch above) so a burst of simultaneous alerts does one
    // round of state/sound/notification/refresh work instead of one per
    // alert. No per-alert toast: alerts already surface via the browser push
    // notification and by auto-populating the Alerts page/notification bell
    // in real time.
    socket.on('alert:new', (alert) => {
      pendingAlertBatch.push(alert);
      if (!alertBatchTimer) {
        alertBatchTimer = setTimeout(() => flushAlertBatch(set, get), ALERT_BATCH_WINDOW_MS);
      }
    });

    socket.on('alert:acknowledged', ({ alertId }) => {
      set((state) => ({
        activeAlerts: Math.max(0, state.activeAlerts - 1)
      }));
    });

    socket.on('alert:resolved', ({ alertId }) => {
      set((state) => ({
        activeAlerts: Math.max(0, state.activeAlerts - 1)
      }));
    });

    socket.on('compliance:update', (data) => {
      set({ compliance: data });
    });

    // Debounce device offline/online toasts:
    // - device:offline only shows after 15s of sustained disconnect (hides brief reconnects)
    // - device:online only shows if the user actually SAW the offline toast
    //   (suppresses spurious "online" on config reloads / brief WS flaps that
    //    reconnect within the 30s backend grace period — no offline event is ever
    //    sent in that window, but device:online fires on every WS connect)
    const deviceOfflineTimers = new Map(); // deviceId → { timerId, toastId? }
    const deviceShownOffline = new Set();  // deviceIds whose offline toast is visible

    socket.on('device:offline', ({ deviceId, deviceName }) => {
      if (deviceOfflineTimers.has(deviceId)) {
        clearTimeout(deviceOfflineTimers.get(deviceId).timerId);
      }
      const timerId = setTimeout(() => {
        const toastId = toast.error(`Device offline: ${deviceName}`);
        deviceOfflineTimers.set(deviceId, { timerId: null, toastId });
        deviceShownOffline.add(deviceId);
      }, 15000);
      deviceOfflineTimers.set(deviceId, { timerId, toastId: null });
    });

    socket.on('device:online', ({ deviceId, deviceName }) => {
      if (deviceOfflineTimers.has(deviceId)) {
        const { timerId, toastId } = deviceOfflineTimers.get(deviceId);
        if (timerId) {
          // Came back before the 15s timer fired — suppress both toasts silently
          clearTimeout(timerId);
          deviceOfflineTimers.delete(deviceId);
          return;
        }
        if (toastId) {
          toast.dismiss(toastId);
        }
        deviceOfflineTimers.delete(deviceId);
      }
      // Only show "online" toast if user saw the device go offline
      if (deviceShownOffline.has(deviceId)) {
        deviceShownOffline.delete(deviceId);
        toast.success(`Device online: ${deviceName}`);
      }
    });

    socket.on('device:health', (data) => {
      set((state) => ({
        deviceHealth: {
          ...state.deviceHealth,
          [data.deviceId]: data,
        },
      }));
    });

    // P0-3: Real-time zone occupancy updates
    socket.on('occupancy:update', (data) => {
      set((state) => ({
        zoneOccupancy: {
          ...state.zoneOccupancy,
          [data.siteId]: {
            ...data,
            receivedAt: new Date().toISOString(),
          },
        },
      }));
    });

    // P2-1: Real-time camera status updates
    socket.on('camera:status', (data) => {
      set((state) => ({
        cameraStatuses: {
          ...state.cameraStatuses,
          [data.cameraId]: {
            status: data.status,
            timestamp: data.timestamp,
          },
        },
      }));
    });

    socket.on('error', ({ message }) => {
      console.error('Socket error:', message);
    });

    // Mail alert daily cap approaching/exceeded for a site
    socket.on('mail-quota:warning', (data) => {
      set((state) => ({
        mailQuotaWarnings: [
          ...state.mailQuotaWarnings.filter((w) => w.siteId !== data.siteId),
          data,
        ],
      }));
    });
  },

  disconnect: () => {
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
      socket = null;
    }
    clearTimeout(alertBatchTimer);
    alertBatchTimer = null;
    pendingAlertBatch = [];
    set({
      connected: false,
      activeAlerts: 0,
      latestAlert: null,
      compliance: null,
      subscribedSite: null,
      deviceHealth: {},
      zoneOccupancy: {},
      cameraStatuses: {},
      mailQuotaWarnings: [],
    });
  },

  subscribeSite: (siteId) => {
    if (!siteId) return;

    // Record the desired site regardless of connection state. If the socket
    // hasn't finished its handshake yet (e.g. this runs in the same tick as
    // connect() on page load — the common case, since Layout calls both in
    // effects that fire together), the old code silently no-op'd here and
    // never wrote `subscribedSite`. The `connect` handler below only
    // re-joins a room it finds in `subscribedSite`, so a lost race meant the
    // site room was never joined for the rest of the session — alerts kept
    // being created, but this tab would never receive `alert:new` for them,
    // with no visible error (HTTP-based refreshes still worked normally).
    const prevSite = get().subscribedSite;
    set({ subscribedSite: siteId });

    if (socket?.connected) {
      if (prevSite && prevSite !== siteId) {
        socket.emit('unsubscribe:site', prevSite);
      }
      socket.emit('subscribe:site', siteId);
    }
    // If not connected yet, the `connect` handler joins this site as soon as
    // the handshake completes, since it reads `subscribedSite` from state.
  },

  unsubscribeSite: (siteId) => {
    if (socket?.connected) {
      socket.emit('unsubscribe:site', siteId);
      set({ subscribedSite: null });
    }
  },

  acknowledgeAlert: (alertId) => {
    if (socket?.connected) {
      socket.emit('alert:acknowledge', { alertId });
    }
  },

  requestLatestData: (siteId) => {
    if (socket?.connected) {
      socket.emit('request:latest', { siteId });
    }
  },

  setActiveAlerts: (count) => {
    set({ activeAlerts: count });
  },
}));
